import { makeDictionary, mergeMask, check, arrayFilter, advancedRights, arrayMap } from '@/domain/restriction/engine';
import { RIGHTS, dictionary } from '@/domain/restriction/policies';

import { selector } from '@/lib/selectors';
import * as env from '@/domain/env/envSelector';
import { companyRoleSelector, companyRoleModificatorsSelector } from '@/domain/env/sessionRoleSelectors';
import * as companies from '@/domain/companies/companiesSelector';
import { companyFeatureFlagsSelector } from '@/domain/companies/companiesLeafSelectors';
import * as documents from '@/domain/documents/documentSelector';
import { currentCompanyIdSelector } from '@/domain/router/routerLeafSelectors';
import {
  hasNonDisconnectedPaymentProviderSelector,
  hasConnectedPaymentProviderSelector,
} from '@/domain/paySettings/selectorForPermissions';
import CONST from '@/domain/documents/constants';

import { ROLES } from '@/domain/env/helpers';

export const myRestriction = selector(
  companyRoleSelector,
  companyRoleModificatorsSelector,
  companies.doesCurrentCompanySupportGeneralDocsOnlySelector,
  env.isAdminSelector,
  env.isExpiredSelector,
  documents.documentSelector,
  companyFeatureFlagsSelector,
  env.isAllowFreshdeskSelector,
  currentCompanyIdSelector,
  env.payModuleCompaniesContextSelector,
  env.payModuleCompanyPermissionsSelector,
  hasNonDisconnectedPaymentProviderSelector,
  hasConnectedPaymentProviderSelector,
  (
    e,
    roleModificators,
    dccsgdo,
    isAdmin,
    isExpired,
    currentDocument,
    companyFeatureFlags,
    allowFreshdesk,
    companyId,
    payModuleCompaniesContext,
    payModuleCompanyPermissions,
    hasNonDisconnectedPaymentProvider,
    hasConnectedPaymentProvider,
  ) => {
    return mergeMask(dictionary)(
      []
        .concat([e ? RIGHTS.IS_AUTHORIZED : RIGHTS.IS_NOT_AUTHORIZED])
        .concat(e === ROLES.accountant ? [RIGHTS.IS_ACCOUNT] : [])
        .concat(e === ROLES.accountant && roleModificators.limited ? [RIGHTS.IS_LIMITED_ACCOUNT] : [])
        .concat(
          e === ROLES.accountant && roleModificators.restricted
            ? [RIGHTS.IS_RESTRICTED_ACCOUNTANT]
            : [RIGHTS.IS_NOT_RESTRICTED_ACCOUNTANT],
        )
        .concat(e === ROLES.accountant && roleModificators.readonly ? [RIGHTS.IS_READ_ONLY_ACCOUNTANT] : [])
        .concat(e === ROLES.user ? [RIGHTS.IS_USER] : [RIGHTS.IS_NOT_USER])
        .concat(e === ROLES['confidential-user'] ? [RIGHTS.IS_CONFIDENTIAL_USER] : [RIGHTS.IS_NOT_CONFIDENTIAL_USER])
        .concat(
          e === ROLES.user && roleModificators.restricted
            ? [RIGHTS.IS_RESTRICTED_USER]
            : [RIGHTS.IS_NOT_RESTRICTED_USER],
        )
        .concat(
          e === ROLES['confidential-user'] && roleModificators.restricted
            ? [RIGHTS.IS_CONFIDENTIAL_RESTRICTED_USER]
            : [RIGHTS.IS_NOT_CONFIDENTIAL_RESTRICTED_USER],
        )

        .concat(e === ROLES.supplier ? [RIGHTS.IS_SUPPLIER_USER] : [RIGHTS.IS_NOT_SUPPLIER_USER])
        .concat([dccsgdo ? RIGHTS.IS_COMPANY_SUPPORT_GENERAL_DOC_ONLY : RIGHTS.IS_COMPANY_SUPPORT_FINANCIAL_DOC])
        .concat(isAdmin ? [RIGHTS.IS_ADMIN] : [RIGHTS.IS_NOT_ADMIN])
        .concat(isExpired ? [RIGHTS.IS_EXPIRED] : [])
        .concat(currentDocument.get('doctype') === 'financial' ? [RIGHTS.IS_CURRENT_DOCUMENT_FINANCIAL] : [])
        .concat(
          currentDocument.tags.has(CONST.tags.isAccepted)
            ? [RIGHTS.IS_ACCEPTED_DOCUMENT]
            : [RIGHTS.IS_NOT_ACCEPTED_DOCUMENT],
        )
        .concat(
          currentDocument.tags.has(CONST.tags.isBeingProcessed)
            ? [RIGHTS.IS_DOCUMENT_BEING_PROCESSED]
            : [RIGHTS.IS_NOT_DOCUMENT_BEING_PROCESSED],
        )
        .concat(
          currentDocument.tags.has(CONST.tags.isScheduledAcceptance) ? [RIGHTS.IS_DOCUMENT_SCHEDULED_ACCEPTANCE] : [],
        )
        .concat(
          companyFeatureFlags.get('only_admin_can_change_af', false)
            ? [RIGHTS.ONLY_ADMIN_CAN_CHANGE_AF]
            : [RIGHTS.NOT_ONLY_ADMIN_CAN_CHANGE_AF],
        )
        .concat(allowFreshdesk ? [RIGHTS.ALLOW_FRESHDESK] : []) // this should remain in rights
        .concat(
          companyId && payModuleCompaniesContext.includes(companyId)
            ? [RIGHTS.IS_PAY_MODULE_AVAILABLE_FOR_THIS_COMPANY]
            : [],
        )
        .concat(
          companyFeatureFlags.get('enable_prepare_payment_for_non_published_invoices', false)
            ? [RIGHTS.CAN_PREPARE_PAYMENT_FOR_NON_PUBLISHED_INVOICES]
            : [],
        )
        // TODO: When there are multiple payment providers, refactor this to depend on the selected payment provider
        .concat(payModuleCompanyPermissions.first()?.get('isOwner') ? [RIGHTS.PAY_IS_OWNER] : [RIGHTS.PAY_IS_NOT_OWNER])
        .concat(
          payModuleCompanyPermissions.first()?.get('isController')
            ? [RIGHTS.PAY_IS_CONTROLLER]
            : [RIGHTS.PAY_IS_NOT_CONTROLLER],
        )
        .concat(
          payModuleCompanyPermissions.first()?.get('isApprover')
            ? [RIGHTS.PAY_IS_APPROVER]
            : [RIGHTS.PAY_IS_NOT_APPROVER],
        )
        .concat(
          hasNonDisconnectedPaymentProvider
            ? [RIGHTS.PAY_HAS_NON_DISCONNECTED_PAYMENT_PROVIDER]
            : [RIGHTS.PAY_ALL_DISCONNECTED_PAYMENT_PROVIDER],
        )
        .concat(hasConnectedPaymentProvider ? [RIGHTS.PAY_HAS_CONNECTED_PAYMENT_PROVIDER] : []),
    );
  },
);

export const isGranted = selector(myRestriction, (restriction) => (lvl) => check(lvl, restriction));

export { arrayFilter, makeDictionary, mergeMask, advancedRights, check, arrayMap };
export * from '@/domain/restriction/policies';
